// ============================================
// API SERVICE - COMPLETE WITH PUBLIC ROUTE HANDLING
import { getFreshSupabaseToken } from './tokenRefresh';
// ============================================

// ✅ Environment-aware API URL
const API_URL = process.env.NODE_ENV === 'production' 
    ? 'https://onepurposelogistics-api.onrender.com/api'
    : 'http://localhost:5000/api';

console.log('🌐 API_URL:', API_URL);

// ============================================
// Get auth token from localStorage
// ============================================
const getToken = () => {
    return localStorage.getItem('token');
};

// ============================================
// Get the Supabase access token (RLS identity)
// ============================================
const getSupabaseToken = () => {
    return localStorage.getItem('supabase_token');
};

// ============================================
// Get user from localStorage
// ============================================
const getUser = () => {
    try {
        const user = localStorage.getItem('user');
        return user ? JSON.parse(user) : null;
    } catch {
        return null;
    }
};

// ============================================
// PUBLIC ROUTES - Don't require authentication
// ============================================
const PUBLIC_ROUTES = [
    '/subcontractors',
    '/clients', 
    '/settings',
    '/dashboard/summary',
    '/health'
];

const isPublicRoute = (endpoint) => {
    return PUBLIC_ROUTES.some(route => endpoint.startsWith(route));
};

// ============================================
// Main API call function with automatic token
// ============================================
async function apiCall(endpoint, options = {}) {
    const token = getToken();
    const user = getUser();
    const supabaseToken = await getFreshSupabaseToken();

    const headers = {
        'Content-Type': 'application/json',
        ...options.headers
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    if (supabaseToken) {
        headers['x-supabase-token'] = supabaseToken;
    }

    if (user) {
        headers['X-User-Id'] = user.id;
        headers['X-User-Email'] = user.email;
        headers['X-User-Role'] = user.role;
    }

    const config = {
        ...options,
        headers
    };

    try {
        const response = await fetch(`${API_URL}${endpoint}`, config);
        const data = await response.json();

        if (response.status === 401) {
            if (isPublicRoute(endpoint)) {
                console.warn(`⚠️ Public route ${endpoint} returned 401, but continuing...`);
                return data;
            }
            
            localStorage.removeItem('token');
            localStorage.removeItem('supabase_token');
            localStorage.removeItem('supabase_refresh_token');
            localStorage.removeItem('user');
            localStorage.removeItem('subbieId');
            window.location.href = '/login';
            throw new Error('Session expired. Please login again.');
        }

        if (!response.ok) {
            throw new Error(data.error || 'API call failed');
        }

        return data;
    } catch (error) {
        console.error('❌ API Call Error:', error);
        throw error;
    }
}

// ============================================
// AUTH
// ============================================
export const auth = {
    login: (email, password) => apiCall('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
    }),
    register: (userData) => apiCall('/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData)
    }),
    me: () => apiCall('/users/me'),
    logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('supabase_token');
        localStorage.removeItem('supabase_refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('subbieId');
        window.location.href = '/login';
    }
};

// ============================================
// CLIENTS
// ============================================
export const clients = {
    getAll: () => apiCall('/clients'),
    getOne: (id) => apiCall(`/clients/${id}`),
    create: (data) => apiCall('/clients', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    update: (id, data) => apiCall(`/clients/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    delete: (id) => apiCall(`/clients/${id}`, {
        method: 'DELETE'
    })
};

// ============================================
// SUBCONTRACTORS
// ============================================
export const subcontractors = {
    getAll: () => apiCall('/subcontractors'),
    getOne: (id) => apiCall(`/subcontractors/${id}`),
    create: (data) => apiCall('/subcontractors', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    update: (id, data) => apiCall(`/subcontractors/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    delete: (id) => apiCall(`/subcontractors/${id}`, {
        method: 'DELETE'
    }),
    getPending: () => apiCall('/subcontractors/pending'),
    adminCreate: (data) => apiCall('/subcontractors/admin-create', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    verify: (id, data) => apiCall(`/subcontractors/${id}/verify`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    getByUserId: (userId) => apiCall(`/subcontractors?user_id=${userId}`)
};

// ============================================
// JOBS
// ============================================
export const jobs = {
    getAll: (params = {}) => {
        const query = new URLSearchParams(params).toString();
        return apiCall(`/jobs${query ? `?${query}` : ''}`);
    },
    getActive: () => apiCall('/jobs/active'),
    getOne: (id) => apiCall(`/jobs/${id}`),
    create: (data) => apiCall('/jobs', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    update: (id, data) => apiCall(`/jobs/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    delete: (id) => apiCall(`/jobs/${id}`, {
        method: 'DELETE'
    }),
    restore: (id) => apiCall(`/jobs/${id}/restore`, {
        method: 'POST'
    }),
    getArchived: () => apiCall('/jobs/archived'),
    getByStatus: (status) => apiCall(`/jobs/status/${status}`),
    getByClient: (clientId) => apiCall(`/jobs/client/${clientId}`),
    // Attach the client's Purchase Order after the fact
    uploadPO: (id, data) => apiCall(`/jobs/${id}/po`, {
        method: 'PUT',
        body: JSON.stringify(data)
    })
};

// ============================================
// LOADS
// ============================================
export const loads = {
    getAll: (params = {}) => {
        const query = new URLSearchParams(params).toString();
        return apiCall(`/loads${query ? `?${query}` : ''}`);
    },
    getOne: (id) => apiCall(`/loads/${id}`),
    getByJob: (jobId) => apiCall(`/loads/job/${jobId}`),
    create: (data) => apiCall('/loads', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    update: (id, data) => apiCall(`/loads/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    delete: (id) => apiCall(`/loads/${id}`, {
        method: 'DELETE'
    }),
    updateStatus: (id, status) => apiCall(`/loads/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status })
    }),
    getProgress: (jobId) => apiCall(`/loads/job/${jobId}/progress`),
    getCount: (jobId) => apiCall(`/loads/count/${jobId}`)
};

// ============================================
// TRUCKS
// ============================================
export const trucks = {
    getAll: () => apiCall('/trucks'),
    getOne: (id) => apiCall(`/trucks/${id}`),
    create: (data) => apiCall('/trucks', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    update: (id, data) => apiCall(`/trucks/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    delete: (id) => apiCall(`/trucks/${id}`, {
        method: 'DELETE'
    })
};

// ============================================
// JOB REQUIREMENTS
// ============================================
export const jobRequirements = {
    getAll: () => apiCall('/job-requirements'),
    getByJob: (jobId) => apiCall(`/job-requirements/job/${jobId}`),
    getBySubbie: (subbieId) => apiCall(`/job-requirements/subbie/${subbieId}`),
    create: (data) => apiCall('/job-requirements', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    update: (id, data) => apiCall(`/job-requirements/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    delete: (id) => apiCall(`/job-requirements/${id}`, {
        method: 'DELETE'
    }),
    respond: (id, data) => apiCall(`/job-requirements/${id}/respond`, {
        method: 'PUT',
        body: JSON.stringify(data)
    })
};

// ============================================
// NOTIFICATIONS
// ============================================
export const notifications = {
    getAll: () => apiCall('/notifications'),
    getUnreadCount: () => apiCall('/notifications/unread/count'),
    markAsRead: (id) => apiCall(`/notifications/${id}/read`, {
        method: 'PUT'
    }),
    markAllAsRead: () => apiCall('/notifications/read-all', {
        method: 'PUT'
    }),
    dismiss: (id) => apiCall(`/notifications/${id}/dismiss`, {
        method: 'PUT'
    })
};

// ============================================
// INVOICES
// ============================================
export const invoices = {
    getAll: () => apiCall('/invoices'),
    getOne: (id) => apiCall(`/invoices/${id}`),
    create: (data) => apiCall('/invoices', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    update: (id, data) => apiCall(`/invoices/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    delete: (id) => apiCall(`/invoices/${id}`, {
        method: 'DELETE'
    }),
    // Convert a proforma into a tax invoice (assigns the next INV-00001)
    issue: (id, data = {}) => apiCall(`/invoices/${id}/issue`, {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    // Record a payment (deposit or balance) against the invoice
    recordPayment: (id, data) => apiCall(`/invoices/${id}/payments`, {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    // Invoice statement: payments ledger + balance
    getStatement: (id) => apiCall(`/invoices/${id}/statement`)
};

// ============================================
// SUBBIE INVOICES
// ============================================
export const subbieInvoices = {
    getBySubbie: (subbieId) => apiCall(`/subbie-invoices/subbie/${subbieId}`),
    getAll: () => apiCall('/subbie-invoices/admin'),
    create: (data) => apiCall('/subbie-invoices/create', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    submitPOD: (data) => apiCall('/subbie-invoices/submit-pod', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    receivePOD: (id, data) => apiCall(`/subbie-invoices/${id}/receive-pod`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    pay: (id, data) => apiCall(`/subbie-invoices/${id}/pay`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    getPendingPOD: (subbieId) => apiCall(`/subbie-invoices/pending-pod/${subbieId}`),
    getPODSubmissions: (subbieId) => apiCall(`/subbie-invoices/pod-submissions/${subbieId}`),
    getAdminPODSubmissions: () => apiCall('/subbie-invoices/admin-pod-submissions'),
    getCounts: (subbieId) => apiCall(`/subbie-invoices/count/${subbieId}`)
};

// ============================================
// ADMIN
// ============================================
export const admin = {
    getApprovals: () => apiCall('/admin/approvals'),
    getApprovalsCount: () => apiCall('/admin/approvals/count'),
    approveApproval: (id, data) => apiCall(`/admin/approvals/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    getDashboardStats: () => apiCall('/admin/dashboard-stats')
};

// ============================================
// USERS
// ============================================
export const users = {
    getAll: () => apiCall('/users'),
    getOne: (id) => apiCall(`/users/${id}`),
    create: (data) => apiCall('/users', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    update: (id, data) => apiCall(`/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    delete: (id) => apiCall(`/users/${id}`, {
        method: 'DELETE'
    }),
    getPermissions: (userId) => apiCall(`/user-permissions/${userId}`),
    updatePermissions: (userId, permissions) => apiCall(`/user-permissions/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ permissions })
    })
};

// ============================================
// SETTINGS
// ============================================
export const settings = {
    getAll: () => apiCall('/settings'),
    getByCategory: (category) => apiCall(`/settings/${category}`),
    update: (category, key, value) => apiCall(`/settings/${category}/${key}`, {
        method: 'PUT',
        body: JSON.stringify({ value })
    })
};

// ============================================
// SUBBIE UPLOADS
// ============================================
export const uploads = {
    getBySubbie: (subbieId) => apiCall(`/subbie/uploads/${subbieId}`),
    getBySubbieAndJob: (subbieId, jobId) => apiCall(`/subbie/uploads/${subbieId}/job/${jobId}`),
    upload: async (formData) => {
        const token = getToken();
        const supabaseToken = await getFreshSupabaseToken();
        const headers = {};
        if (supabaseToken) {
            headers['x-supabase-token'] = supabaseToken;
        }
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        return fetch(`${API_URL}/subbie/uploads`, {
            method: 'POST',
            headers,
            body: formData
        }).then(res => res.json());
    },
    verify: (id, data) => apiCall(`/subbie/uploads/${id}/verify`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    delete: (id) => apiCall(`/subbie/uploads/${id}`, {
        method: 'DELETE'
    })
};

// ============================================
// AUDIT LOG
// ============================================
export const audit = {
    getAll: (params = {}) => {
        const query = new URLSearchParams(params).toString();
        return apiCall(`/audit-log${query ? `?${query}` : ''}`);
    },
    create: (data) => apiCall('/audit-log', {
        method: 'POST',
        body: JSON.stringify(data)
    })
};

// ============================================
// SUPPORT - in-app support form → emails info@onepurposelogistics.com
// ============================================
export const support = {
    submit: (data) => apiCall('/support', {
        method: 'POST',
        body: JSON.stringify(data)
    })
};

// ============================================
// DASHBOARD
// ============================================
export const dashboard = {
    getSummary: () => apiCall('/dashboard/summary')
};

// ============================================
// SUBBIE
// ============================================
export const subbie = {
    getRequirements: (subbieId) => apiCall(`/subbie/requirements/${subbieId}`),
    getAllRequirements: (subbieId) => apiCall(`/subbie/all-requirements/${subbieId}`),
    getFleet: (subbieId) => apiCall(`/subbie/fleet/${subbieId}`),
    allocateFleet: (data) => apiCall('/subbie/allocate-fleet', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    getScheduledLoads: (subbieId) => apiCall(`/subbie/scheduled-loads/${subbieId}`),
    getHistory: (subbieId) => apiCall(`/subbie/history/${subbieId}`)
};

// ============================================
// FLEET AVAILABILITY
// ============================================
export const fleet = {
    getAvailability: (subbieId, startDate, endDate) => {
        const query = new URLSearchParams({ start_date: startDate, end_date: endDate }).toString();
        return apiCall(`/fleet/availability/${subbieId}?${query}`);
    }
};

// ============================================
// MESSAGES
// ============================================
export const messages = {
    getConversations: () => apiCall('/messages/conversations'),
    getMessages: (userId) => apiCall(`/messages/${userId}`),
    send: (data) => apiCall('/messages', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    markAsRead: (messageId) => apiCall(`/messages/${messageId}/read`, {
        method: 'PUT'
    }),
    getUnreadCount: () => apiCall('/messages/unread/count')
};

// ============================================
// QUOTES (New Quoting System)
// ============================================
export const quotes = {
    getAll: (params = {}) => {
        const query = new URLSearchParams(params).toString();
        return apiCall(`/quotes${query ? `?${query}` : ''}`);
    },
    getOne: (id) => apiCall(`/quotes/${id}`),
    create: (data) => apiCall('/quotes', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    update: (id, data) => apiCall(`/quotes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    send: (id, emailData = {}) => apiCall(`/quotes/${id}/send`, {
        method: 'POST',
        body: JSON.stringify(emailData)
    }),
    approve: (id, data = {}) => apiCall(`/quotes/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    duplicate: (id) => apiCall(`/quotes/${id}/duplicate`, {
        method: 'POST'
    }),
    reject: (id, data = {}) => apiCall(`/quotes/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    cancel: (id) => apiCall(`/quotes/${id}/cancel`, {
        method: 'POST'
    }),
    delete: (id) => apiCall(`/quotes/${id}`, {
        method: 'DELETE'
    }),
    clientRequest: (data) => apiCall('/client/quotes/request', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    clientAccept: (id, data = {}) => apiCall(`/quotes/${id}/client-accept`, {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    getStats: () => apiCall('/quotes/stats'),
    getRegister: () => apiCall('/quotes/register'),
    createRequest: (data) => apiCall('/quotes/request', {
        method: 'POST',
        body: JSON.stringify(data)
    })
};

// ============================================
// CLIENT PORTAL - quotes (client-scoped, internal pricing stripped)
// ============================================
export const clientDashboard = {
    get: () => apiCall('/client/dashboard'),
    // Jeeves' briefing. Cached per client per day, so calling this on every page load is cheap;
    // `regenerate: true` asks him to think again (and is rate-limited server-side).
    brief: (regenerate = false) => apiCall('/client/brief', {
        method: 'POST',
        body: JSON.stringify({ regenerate: regenerate === true })
    })
};

export const clientReports = {
    pack: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.from) qs.set('from', params.from);
        if (params.to) qs.set('to', params.to);
        const q = qs.toString();
        return apiCall(`/client/reports/pack${q ? `?${q}` : ''}`);
    }
};

export const clientPods = {
    list: () => apiCall('/client/pods')
};

export const clientQuotes = {
    getAll: () => apiCall('/client/quotes'),
    getOne: (id) => apiCall(`/client/quotes/${id}`),
    request: (data) => apiCall('/client/quotes/request', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    accept: (id, data = {}) => apiCall(`/client/quotes/${id}/accept`, {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    reject: (id, data = {}) => apiCall(`/client/quotes/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify(data)
    })
};

// ============================================
// CLIENT PORTAL - invoices + proformas (client-scoped, no cost price)
// ============================================
export const clientInvoices = {
    getAll: () => apiCall('/client/invoices'),
    getOne: (id) => apiCall(`/client/invoices/${id}`)
};

// ============================================
// SITE INSPECTIONS (Phase 3 / 3.5)
// ============================================
export const siteInspections = {
    // Admin: request a site inspection for a quote (standard | custom | inspector)
    request: (quote_id, data = {}) => apiCall('/site-inspections/request', {
        method: 'POST',
        body: JSON.stringify({ quote_id, ...data })
    }),

    // Admin: list inspections for a quote
    getByQuote: (quoteId) => apiCall(`/site-inspections/quote/${quoteId}`),

    // Admin: delete an inspection record
    remove: (id) => apiCall(`/site-inspections/${id}`, {
        method: 'DELETE'
    }),

    // Public: get inspection by token (NO auth required)
    getByToken: (token) => fetch(`${API_URL}/site-inspections/public/${token}`).then(res => res.json()),

    // Public: submit inspection answers + files (NO auth required)
    submitByToken: (token, data) => fetch(`${API_URL}/site-inspections/public/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
    }).then(res => res.json()),

    // Public: inspector submits the digital rigging site report (NO auth required)
    submitReportByToken: (token, data) => fetch(`${API_URL}/site-inspections/public/${token}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
    }).then(res => res.json())
};

// ============================================
// SITE INSPECTORS (Phase 3.5)
// ============================================
export const siteInspectors = {
    getAll: (params = {}) => {
        const query = new URLSearchParams(params).toString();
        return apiCall(`/site-inspectors${query ? `?${query}` : ''}`);
    },
    create: (data) => apiCall('/site-inspectors', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    update: (id, data) => apiCall(`/site-inspectors/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    remove: (id) => apiCall(`/site-inspectors/${id}`, {
        method: 'DELETE'
    })
};

// ============================================
// SITE UPLOADS (Supabase Storage - images/pdf/video)
// ============================================
export const siteUploads = {
    upload: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return fetch(`${API_URL}/site-uploads`, {
            method: 'POST',
            body: formData
        }).then(res => res.json());
    }
};

// ============================================
// RATE CARDS (Phase 4 - Benecke Container Engine)
// ============================================
export const rateCards = {
    // Search towns in Benecke's lookup table
    getTowns: (params = {}) => {
        const query = new URLSearchParams(params).toString();
        return apiCall(`/rate-cards/towns${query ? `?${query}` : ''}`);
    },

    // Truck configs + add-on rates (Benecke columns D-I)
    getTrucks: () => apiCall('/rate-cards/trucks'),

    // Admin knobs (fuel price, margin, border surcharge, fuel base)
    getVariables: () => apiCall('/rate-cards/variables'),

    // Editable add-ons (TBC flagged)
    getAddons: () => apiCall('/rate-cards/addons'),

    // Zone bands (local / intra-regional / regional)
    getZones: () => apiCall('/rate-cards/zones'),

    // THE CORE ENGINE - calculate a quote from wizard inputs
    calculate: (data) => apiCall('/rate-cards/calculate', {
        method: 'POST',
        body: JSON.stringify(data)
    }),

    // DeepSeek AI assistant - natural language -> structured wizard fields
    // Pass the full conversation (history) + accumulated wizard state so the AI remembers.
    assist: (message, currentWizard = {}, history = []) => apiCall('/rate-cards/assist', {
        method: 'POST',
        body: JSON.stringify({ message, currentWizard, history })
    }),

    // Update admin knobs
    updateVariables: (data) => apiCall('/rate-cards/variables', {
        method: 'PUT',
        body: JSON.stringify(data)
    }),

    // Update an add-on
    updateAddon: (id, data) => apiCall(`/rate-cards/addons/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),

    // Create a new add-on
    createAddon: (data) => apiCall('/rate-cards/addons', {
        method: 'POST',
        body: JSON.stringify(data)
    }),

    // Add a new town (with Google Maps distance)
    addTown: (data) => apiCall('/rate-cards/towns', {
        method: 'POST',
        body: JSON.stringify(data)
    })
};

// ============================================
// QUICKBOOKS
// ============================================
export const quickbooks = {
    getStatus: () => apiCall('/quickbooks/status'),
    getConnectUrl: () => apiCall('/quickbooks/connect'),
    disconnect: () => apiCall('/quickbooks/disconnect', {
        method: 'POST'
    }),
    sync: (types) => apiCall('/quickbooks/sync', {
        method: 'POST',
        body: JSON.stringify({ types })
    }),
    getSyncLog: (limit = 50) => apiCall(`/quickbooks/sync-log?limit=${limit}`)
};

// ============================================
// BANK DETAILS (subbie guardrail)
// ============================================
export const bankDetails = {
    getStatus: () => apiCall('/bank-details/status'),
    submit: (data) => apiCall('/bank-details/submit', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    confirm: (data) => apiCall('/bank-details/confirm', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    override: (data) => apiCall('/bank-details/override', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    pushToQbo: (data) => apiCall('/bank-details/push-to-qbo', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    getHistory: (subcontractorId) => apiCall(`/bank-details/history${subcontractorId ? '?subcontractor_id=' + subcontractorId : ''}`),
    getTerms: () => apiCall('/bank-details/terms')
};

// ============================================
// ACCOUNTS (overview + reports)
// ============================================
export const accounts = {
    getOverview: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.month) qs.set('month', params.month);
        if (params.months) qs.set('months', params.months);
        const suffix = qs.toString() ? '?' + qs.toString() : '';
        return apiCall(`/accounts/overview${suffix}`);
    },
    getTurnoverProfit: (months = 6) => apiCall(`/accounts/turnover-profit?months=${months}`),
    getSalesBySalesperson: (months = 6) => apiCall(`/accounts/sales-by-salesperson?months=${months}`)
};

// ============================================
// PAYMENT TERMS (admin-only maintenance)
// ============================================
export const paymentTerms = {
    getList: () => apiCall('/payment-terms/list'),
    updateClient: (id, data) => apiCall(`/payment-terms/client/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    updateSubcontractor: (id, data) => apiCall(`/payment-terms/subcontractor/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    })
};

// ============================================
// COMPANY PROFILE (single source + change control)
// ============================================
export const companyProfile = {
    get: () => apiCall('/company-profile'),
    verifyPasscode: (passcode) => apiCall('/company-profile/verify-passcode', {
        method: 'POST',
        body: JSON.stringify({ passcode })
    }),
    submitChange: (changes, passcode) => apiCall('/company-profile/submit-change', {
        method: 'POST',
        body: JSON.stringify({ changes, passcode })
    }),
    getChanges: (limit = 50) => apiCall(`/company-profile/changes?limit=${limit}`),
    approveChange: (id, passcode) => apiCall(`/company-profile/changes/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ passcode })
    }),
    rejectChange: (id, passcode, notes) => apiCall(`/company-profile/changes/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ passcode, notes })
    })
};

// ============================================
// DEFAULT EXPORT
// ============================================
export default {
    auth,
    clients,
    subcontractors,
    jobs,
    loads,
    trucks,
    jobRequirements,
    notifications,
    invoices,
    subbieInvoices,
    admin,
    users,
    settings,
    uploads,
    audit,
    dashboard,
    subbie,
    fleet,
    messages,
    quotes,
    siteInspections,
    siteInspectors,
    siteUploads,
    rateCards,
    quickbooks,
    bankDetails,
    accounts,
    paymentTerms,
    companyProfile
};
